import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  XMarkIcon,
  PlayIcon,
  ArrowPathRoundedSquareIcon,
} from '@heroicons/react/24/outline';
import { useWorkflowTemplates } from '@/features/workflows/hooks/useWorkflowTemplates';
import { useWorkflowInstances } from '@/features/workflows/hooks/useWorkflowInstances';
import { useWorkflowEngine } from '@/features/workflows/hooks/useWorkflowEngine';
import StartWorkflowModal from '@/features/workflows/components/StartWorkflowModal';
import WorkflowStepProgress from '@/features/workflows/components/WorkflowStepProgress';
import { ROUTES } from '@/utils/constants';
import { ConfirmDialog } from '@/components/ui';
import { useToast } from '@/components/feedback/ToastSystem';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

/**
 * WorkflowTemplatesPanel — substrate slide-out for flows.
 *
 * Operates flows inline:
 *   - "+ Start" launches the template picker → spins up an instance.
 *   - Running tab: live instances with step timeline, cancel inline.
 *   - Templates tab: toggle active/inactive in place; one-click start.
 * Heavy editing (steps, triggers, schema) lives at /workflow-templates/:id.
 */

export default function WorkflowTemplatesPanel() {
  const {
    templates: visibleTemplates,
    allTemplates,
    loading: templatesLoading,
    filter,
    setFilter,
    stats,
    toggleActive,
  } = useWorkflowTemplates();
  const {
    instances,
    loading: instancesLoading,
    stats: instanceStats,
    refetch: refetchInstances,
  } = useWorkflowInstances();
  const { cancelWorkflow } = useWorkflowEngine();
  const { showSuccess, showError } = useToast();

  const [tab, setTab] = useState('running');
  const [expandedInstanceId, setExpandedInstanceId] = useState(null);
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [startPreselect, setStartPreselect] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const activeRunning = useMemo(
    () => instances.filter((i) => i.status === 'active'),
    [instances]
  );

  const handleOpenStart = (template) => {
    setStartPreselect(template ? { templateId: template.id } : null);
    setStartModalOpen(true);
  };

  const handleStarted = async () => {
    setStartModalOpen(false);
    setStartPreselect(null);
    setTab('running');
    await refetchInstances();
    showSuccess('Flow started', 'It now appears under Running.', 3000);
  };

  const handleRequestCancel = (instance) => {
    setCancelTarget(instance);
  };

  const performCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelWorkflow(cancelTarget.id);
      showSuccess('Flow cancelled', '', 2500);
      await refetchInstances();
    } catch (err) {
      console.error('[WorkflowTemplatesPanel] cancel failed:', err);
      showError('Could not cancel flow', err.message || '');
    } finally {
      setCancelling(false);
      setCancelTarget(null);
    }
  };

  const handleToggleActive = async (template) => {
    setTogglingId(template.id);
    try {
      await toggleActive(template.id, !template.active);
    } catch (err) {
      console.error('[WorkflowTemplatesPanel] toggleActive failed:', err);
      showError('Could not update flow', err.message || '');
    } finally {
      setTogglingId(null);
    }
  };

  const renderRunning = () => {
    if (instancesLoading) {
      return (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      );
    }
    if (activeRunning.length === 0) {
      return (
        <p className="text-sm text-base-content/50 py-6 text-center">
          No flows running.{' '}
          <button
            type="button"
            className="link link-primary"
            onClick={() => handleOpenStart()}
          >
            Start one
          </button>
          .
        </p>
      );
    }
    return (
      <div className="space-y-1">
        {activeRunning.map((inst) => {
          const isExpanded = expandedInstanceId === inst.id;
          const progress = inst.totalSteps > 0
            ? Math.round((inst.completedSteps / inst.totalSteps) * 100)
            : 0;
          return (
            <div key={inst.id} className="card bg-base-100 border border-base-300">
              <button
                type="button"
                onClick={() => setExpandedInstanceId(isExpanded ? null : inst.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left
                  ${isExpanded ? 'bg-base-200' : 'hover:bg-base-200'}`}
                aria-expanded={isExpanded}
              >
                {isExpanded ? (
                  <ChevronDownIcon className="w-3.5 h-3.5 flex-none text-base-content/40" />
                ) : (
                  <ChevronRightIcon className="w-3.5 h-3.5 flex-none text-base-content/40" />
                )}
                <ArrowPathRoundedSquareIcon className="w-4 h-4 flex-none text-info" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">
                    {inst.templateName || 'Untitled flow'}
                  </div>
                  <div className="text-xs text-base-content/50 truncate">
                    {inst.context?.studentName ? `${inst.context.studentName} · ` : ''}
                    {inst.completedSteps}/{inst.totalSteps} steps
                  </div>
                </div>
                <span className="text-xs text-base-content/50 tabular-nums">{progress}%</span>
              </button>
              {isExpanded && (
                <div className="border-t border-base-200 px-3 py-3 space-y-3 text-xs">
                  <progress
                    className="progress progress-info w-full"
                    value={progress}
                    max="100"
                  />
                  <WorkflowStepProgress stepInstances={inst.stepInstances} />
                  <div className="flex items-center justify-between pt-1 border-t border-base-200">
                    <span className="text-base-content/40">
                      Started {inst.createdAt?.toDate?.()?.toLocaleDateString?.() || '—'}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs text-error"
                      onClick={() => handleRequestCancel(inst)}
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderTemplates = () => {
    if (templatesLoading) {
      return (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      );
    }
    if (allTemplates.length === 0) {
      return (
        <p className="text-sm text-base-content/50 py-6 text-center">
          No flows created yet.{' '}
          <Link to={`${ROUTES.WORKFLOW_TEMPLATES}/new`} className="link link-primary">
            Create one
          </Link>
          .
        </p>
      );
    }
    return (
      <>
        <div className="flex gap-1 text-xs">
          {[
            { key: 'active', label: `Active ${stats.active}` },
            { key: 'inactive', label: `Inactive ${stats.inactive}` },
            { key: 'all', label: `All ${stats.total}` },
          ].map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`btn btn-xs ${filter === f.key ? 'btn-primary' : 'btn-ghost'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {visibleTemplates.length === 0 ? (
          <p className="text-sm text-base-content/50 py-6 text-center">
            No {filter} flows.
          </p>
        ) : (
          <div className="space-y-1">
            {visibleTemplates.map((tpl) => {
              const isExpanded = expandedTemplateId === tpl.id;
              const stepCount = tpl.steps?.length || 0;
              const trigger = tpl.trigger?.type || 'manual';
              const isToggling = togglingId === tpl.id;
              return (
                <div key={tpl.id} className="card bg-base-100 border border-base-300">
                  <div
                    className={`flex items-center gap-2 px-3 py-2
                      ${isExpanded ? 'bg-base-200' : 'hover:bg-base-200'}`}
                  >
                    <button
                      type="button"
                      onClick={() => setExpandedTemplateId(isExpanded ? null : tpl.id)}
                      className="flex-1 flex items-center gap-2 text-left min-w-0"
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? (
                        <ChevronDownIcon className="w-3.5 h-3.5 flex-none text-base-content/40" />
                      ) : (
                        <ChevronRightIcon className="w-3.5 h-3.5 flex-none text-base-content/40" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {tpl.name || 'Untitled flow'}
                        </div>
                        <div className="text-xs text-base-content/50 truncate">
                          {stepCount} step{stepCount === 1 ? '' : 's'} · {trigger} trigger
                        </div>
                      </div>
                    </button>
                    <input
                      type="checkbox"
                      className="toggle toggle-xs toggle-primary"
                      checked={tpl.active !== false}
                      disabled={isToggling}
                      onChange={() => handleToggleActive(tpl)}
                      aria-label={`Toggle ${tpl.name} active`}
                    />
                  </div>
                  {isExpanded && (
                    <div className="border-t border-base-200 px-3 py-2 space-y-2 text-xs">
                      {tpl.description && (
                        <p className="text-base-content/70">{tpl.description}</p>
                      )}
                      {stepCount === 0 ? (
                        <p className="text-base-content/40 italic">No steps yet</p>
                      ) : (
                        <ol className="space-y-0.5 text-base-content/70">
                          {tpl.steps.slice(0, 5).map((step, idx) => (
                            <li key={step.id || idx} className="flex items-center gap-1.5">
                              <span className="text-base-content/40 tabular-nums w-4 text-right">
                                {idx + 1}.
                              </span>
                              <span className="truncate">
                                {step.name || step.label || step.title || 'Untitled step'}
                              </span>
                            </li>
                          ))}
                          {stepCount > 5 && (
                            <li className="text-base-content/40 pl-5">+{stepCount - 5} more</li>
                          )}
                        </ol>
                      )}
                      <div className="flex items-center gap-2 pt-1">
                        <button
                          type="button"
                          className="btn btn-primary btn-xs"
                          onClick={() => handleOpenStart(tpl)}
                          disabled={tpl.active === false || stepCount === 0}
                        >
                          <PlayIcon className="w-3.5 h-3.5" />
                          Start
                        </button>
                        <Link
                          to={`${ROUTES.WORKFLOW_TEMPLATES}/${tpl.id}`}
                          className="btn btn-ghost btn-xs"
                        >
                          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                          Edit
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="p-4 pb-24 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-base-content/70 truncate">
          {instanceStats.active} running · {stats.active} active flows
        </h3>
        <button
          type="button"
          className="btn btn-primary btn-xs gap-1"
          onClick={() => handleOpenStart()}
        >
          <PlusIcon className="w-3.5 h-3.5" />
          Start
        </button>
      </div>

      <div role="tablist" className="tabs tabs-bordered tabs-sm">
        <button
          type="button"
          role="tab"
          className={`tab ${tab === 'running' ? 'tab-active' : ''}`}
          onClick={() => setTab('running')}
        >
          Running
          {instanceStats.active > 0 && (
            <span className="badge badge-info badge-xs ml-1">{instanceStats.active}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          className={`tab ${tab === 'templates' ? 'tab-active' : ''}`}
          onClick={() => setTab('templates')}
        >
          Templates
        </button>
      </div>

      {tab === 'running' ? renderRunning() : renderTemplates()}

      <div className="text-xs text-base-content/50 flex items-center justify-end pt-2">
        <Link
          to={ROUTES.WORKFLOW_TEMPLATES}
          className="link link-hover inline-flex items-center gap-1"
        >
          <ArrowTopRightOnSquareIcon className="w-3 h-3" />
          Open in Flows
        </Link>
      </div>

      <StartWorkflowModal
        isOpen={startModalOpen}
        onClose={() => { setStartModalOpen(false); setStartPreselect(null); }}
        onStarted={handleStarted}
        preselectedContext={startPreselect || {}}
      />

      {cancelTarget && (
        <ConfirmDialog
          isOpen
          onClose={() => setCancelTarget(null)}
          onConfirm={performCancel}
          title={`Cancel "${cancelTarget.templateName}"?`}
          message={
            cancelTarget.completedSteps > 0
              ? `This flow is ${cancelTarget.completedSteps} of ${cancelTarget.totalSteps} steps in. Cancelling stops it where it is.`
              : 'This flow has not progressed yet. Cancelling will end it.'
          }
          confirmText={cancelling ? 'Cancelling…' : 'Cancel flow'}
          variant="danger"
        />
      )}
    </div>
  );
}
